import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, Marker, Polyline, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Maximize2, Radio, WifiOff } from "lucide-react";
import { api } from "../lib/api";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const TILE_URL = process.env.REACT_APP_MAP_TILE_URL || "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTRIBUTION = process.env.REACT_APP_MAP_ATTRIBUTION || "&copy; OpenStreetMap contributors";

function markerIcon(letter, tone, title) {
  return L.divIcon({
    className: "mml-map-marker",
    html: `<span aria-label="${title}" style="display:flex;width:32px;height:32px;align-items:center;justify-content:center;border-radius:9999px;background:${tone};border:3px solid #fff;color:#fff;font-weight:800;box-shadow:0 3px 14px rgba(15,23,42,.3)">${letter}</span>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

function liveIcon() {
  return L.divIcon({
    className: "mml-map-live-marker",
    html: '<span aria-label="Position actuelle du conducteur" style="display:block;width:20px;height:20px;border-radius:9999px;background:#2563eb;border:4px solid white;box-shadow:0 0 0 7px rgba(37,99,235,.22),0 3px 14px rgba(15,23,42,.35)"></span>',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

function FitMissionBounds({ pickup, delivery, livePosition }) {
  const map = useMap();
  useEffect(() => {
    const points = [[pickup.lat, pickup.lng], [delivery.lat, delivery.lng]];
    if (livePosition) points.push([livePosition.lat, livePosition.lng]);
    map.fitBounds(points, { padding: [34, 34], maxZoom: 15, animate: false });
  }, [map, pickup.lat, pickup.lng, delivery.lat, delivery.lng, livePosition]);
  return null;
}

function MapView({ pickup, delivery, route, livePosition }) {
  const fallbackLine = [[pickup.lat, pickup.lng], [delivery.lat, delivery.lng]];
  const roadLine = route?.map(([lng, lat]) => [lat, lng]);
  const center = [(pickup.lat + delivery.lat) / 2, (pickup.lng + delivery.lng) / 2];

  return (
    <MapContainer center={center} zoom={6} scrollWheelZoom className="h-full w-full" preferCanvas>
      <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
      <FitMissionBounds pickup={pickup} delivery={delivery} livePosition={livePosition} />
      <Polyline
        positions={roadLine || fallbackLine}
        pathOptions={{ color: "#1F4032", weight: 4, opacity: roadLine ? 0.9 : 0.45, dashArray: roadLine ? undefined : "8 9" }}
      />
      <Marker position={[pickup.lat, pickup.lng]} icon={markerIcon("A", "#1F4032", "Enlèvement")} />
      <Marker position={[delivery.lat, delivery.lng]} icon={markerIcon("B", "#15302A", "Livraison")} />
      {livePosition && (
        <Marker position={[livePosition.lat, livePosition.lng]} icon={liveIcon()} zIndexOffset={1000} />
      )}
    </MapContainer>
  );
}

function formatLastSeen(value) {
  if (!value) return "En attente du premier point GPS";
  const seconds = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 10) return "Position reçue à l'instant";
  if (seconds < 60) return `Position reçue il y a ${seconds} s`;
  return `Dernier signal il y a ${Math.floor(seconds / 60)} min`;
}

export default function MissionMap({ mission }) {
  const [route, setRoute] = useState(null);
  const [open, setOpen] = useState(false);
  const [tracking, setTracking] = useState({
    tracking_active: Boolean(mission.tracking_active),
    tracking_live: Boolean(mission.tracking_live),
    position: mission.transporter_position || null,
    last_seen_at: mission.tracking_last_seen_at || mission.transporter_position?.updated_at || null,
  });

  const pickup = useMemo(() => ({
    lat: Number(mission.pickup_lat), lng: Number(mission.pickup_lng), address: mission.pickup_address,
  }), [mission.pickup_lat, mission.pickup_lng, mission.pickup_address]);
  const delivery = useMemo(() => ({
    lat: Number(mission.delivery_lat), lng: Number(mission.delivery_lng), address: mission.delivery_address,
  }), [mission.delivery_lat, mission.delivery_lng, mission.delivery_address]);

  useEffect(() => {
    if (!mission.transporter_id || !["accepted", "loaded", "en_route"].includes(mission.status)) {
      setTracking((current) => ({ ...current, tracking_active: false, tracking_live: false, position: null }));
      return undefined;
    }
    let stopped = false;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/missions/${mission.id}/tracking`);
        if (!stopped) setTracking(data);
      } catch (_) {
        if (!stopped) setTracking((current) => ({ ...current, tracking_live: false, position: null }));
      }
      if (!stopped) timer = window.setTimeout(poll, 10000);
    };
    poll();
    return () => {
      stopped = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [mission.id, mission.status, mission.transporter_id]);

  useEffect(() => {
    if (!MAPBOX_TOKEN) return undefined;
    let aborted = false;
    const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${pickup.lng},${pickup.lat};${delivery.lng},${delivery.lat}?geometries=geojson&overview=full&access_token=${MAPBOX_TOKEN}`;
    fetch(url)
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("route unavailable")))
      .then((data) => {
        const coordinates = data?.routes?.[0]?.geometry?.coordinates;
        if (!aborted && coordinates) setRoute(coordinates);
      })
      .catch(() => {});
    return () => { aborted = true; };
  }, [pickup.lat, pickup.lng, delivery.lat, delivery.lng]);

  const map = <MapView pickup={pickup} delivery={delivery} route={route} livePosition={tracking.position} />;
  const trackingLabel = tracking.tracking_live
    ? formatLastSeen(tracking.position?.updated_at || tracking.last_seen_at)
    : tracking.tracking_active
      ? "Partage activé — signal GPS momentanément indisponible"
      : "Le conducteur ne partage pas sa position";

  return (
    <>
      <div data-testid="mission-map-thumb" className="relative block w-full h-56 overflow-hidden border border-[#1F4032]/10 rounded-md bg-neutral-100">
        {!open && map}
        <div className="absolute left-2 top-2 z-[500] max-w-[calc(100%-3.5rem)] rounded-md bg-white/95 px-2.5 py-2 text-xs shadow-md backdrop-blur">
          <div className="flex items-center gap-1.5 font-semibold text-[#15302A]">
            {tracking.tracking_live ? <Radio className="h-3.5 w-3.5 text-blue-600" /> : <WifiOff className="h-3.5 w-3.5 text-neutral-500" />}
            {tracking.tracking_live ? "Conducteur en direct" : "Suivi conducteur"}
          </div>
          <div className="mt-0.5 text-[11px] text-neutral-600">{trackingLabel}</div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Agrandir la carte"
          className="absolute top-2 right-2 z-[500] bg-[#1F4032] text-white text-[11px] font-medium px-2 py-1.5 rounded-md flex items-center gap-1 opacity-95 hover:opacity-100"
        >
          <Maximize2 className="w-3 h-3" /> Agrandir
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl p-0 overflow-hidden" data-testid="mission-map-modal">
          <DialogTitle className="sr-only">Carte du trajet et position du conducteur</DialogTitle>
          <div className="h-[75vh] w-full">{open && map}</div>
        </DialogContent>
      </Dialog>
    </>
  );
}
